import { snapshot } from "valtio";
import router from "next/router";

import { Logger } from "common/middleware/logger";

import {
  IListingLookupDto,
  ListingDto,
  DealDto,
  EditDealCommand,
  LanguageType,
  PaginatedResultOfProjectLookupDto,
  ProjectDto,
  UserDto,
  PaginatedResultOfUserDto,
  UserType,
} from "common/middleware/api.services";

import { ApiClientFactory } from "common/middleware/api.client-factory";
import store from "common/middleware/store";

import { mutatePurchaseOffer } from "common/middleware/hooks";
import { callApiAndHandleApiException, getPurchaseOfferIdOrThrow } from "common/middleware/api.helpers";
import { getLocale } from "common/middleware/localization";

export const editPurchaseOffer = async (unitNumber?: number): Promise<void> => {
  if (unitNumber) {
    Logger.debug("Updating purchase offer with new unit ", unitNumber);
  }

  let purchaseOffersClient = await ApiClientFactory.getPurchaseOffersClient();

  await callApiAndHandleApiException(
    async () =>
      await purchaseOffersClient.edit(
        store.purchaseOfferId || "",
        new EditDealCommand({
          dealId: store.purchaseOfferId || "",
          selectedListingId: unitNumber,
          preferredLanguage: getLocale().includes("fr")
            ? LanguageType.French
            : LanguageType.English,
        })
      ),
    false
  );

  const purchaseOffer = await getPurchaseOffer(
    store.purchaseOfferId,
    getLocale()
  );

  mutatePurchaseOffer(purchaseOffer);
};

export const getPurchaseOfferAdmin = async (
  purchaseOfferId: string,
  locale: string
) => {
  let purchaseOffersClient = await ApiClientFactory.getPurchaseOffersClient();

  return await Logger.debugPerformanceAsync("getPurchaseOffer", async () => {
    return await purchaseOffersClient.get(
      purchaseOfferId || "",
      "purchasers,selections,inclusions",
      locale
    );
  });
};

export const getExistingPurchaseOffer = async (): Promise<DealDto> => {
  const snap = snapshot(store);

  Logger.debug("Getting existing purchase offer", { purchaseOfferId: snap.purchaseOfferId });

  if (snap.purchaseOfferId
    && router.query.purchaseOfferId
    && snap.purchaseOfferId !== router.query.purchaseOfferId
  ) {
    Logger.debug("Purchase offer ID mismatch. Waiting for state to update.");
    return null;
  }

  let purchaseOfferId = snap.purchaseOfferId;

  if (!purchaseOfferId) {
    Logger.debug("No pending purchase offer");
    return null;
  }

  const purchaseOffer = await getPurchaseOffer(purchaseOfferId, getLocale());

  if (!purchaseOffer) {
    Logger.error(`Purchase offer with id ${purchaseOfferId} not found`);
    return null;
  }

  Logger.debug(
    `Purchase offer has selected property ${purchaseOffer.selectedListingId}`
  );
  return purchaseOffer;
};

export const getProperties = async (
  key: string,
  projectId?: string,
  phaseId?: string,
  buildingId?: string,
  builderId?: number,
): Promise<IListingLookupDto[]> => {
  Logger.debug("Getting properties");

  let propertiesClient = await ApiClientFactory.getPropertiesClient();

  let properties = await callApiAndHandleApiException(
    async () =>
      await propertiesClient.getPage(
        Number.parseInt(projectId), // projectId
        phaseId, // phaseId
        buildingId, // buildingId
        builderId, // builderId
        undefined, // onlyShowUserProperties
        getLocale(), // locale
        undefined, // filterByListingStates
        undefined, // filterByListingTiers
        undefined, // ownedByUserId
        undefined, // onlyArchived
        undefined, // includedFields
        undefined, // sortOrder
        undefined, // searchString
        undefined, // page
        undefined, // ascSortDirection
        undefined, // pageSize
        true, // shouldRetrieveAllEntities
      )
  );

  return properties.entities ?? [];
};

export const changeSelections = async (
  selectedSelectionId: string,
  selectionId: string,
  cancelledId: string,
  purchaseOfferId?: string,
): Promise<boolean> => {
  purchaseOfferId = purchaseOfferId || getPurchaseOfferIdOrThrow();

  await Logger.debugPerformanceAsync("changeSelections", async () => {
    Logger.debug("Changing selections");

    let purchaseOffersClient = await ApiClientFactory.getPurchaseOffersClient();
    const selectedId = undefined; // TODO Backend needs to be reworked

    if (selectedId === undefined) {
      throw "Failed to update selections";
    }

    Logger.debug("Changed selections");
  });

  mutatePurchaseOffer();

  return true;
};

export const getFile = async (fileId: string) => {
  let filesClient = await ApiClientFactory.getFilesClient();

  let file = await callApiAndHandleApiException(
    async () => await filesClient.get(fileId)
  );

  return file;
};

export const getVerificationIds = async (
  purchaseOfferId: string,
  purchaserId: string
): Promise<string[]> => {
  const purchaseOffersClient = await ApiClientFactory.getPurchaseOffersClient();

  const verificationId = await callApiAndHandleApiException(
    async () =>
      await purchaseOffersClient.getVerificationDocumentIds(
        purchaseOfferId,
        purchaserId
      )
  );

  return verificationId;
};

export const getProperty = async (
  key: string,
  unitId: string,
  draft?: boolean
): Promise<ListingDto> => {
  Logger.debug("Getting unit", { id: unitId, draft: !!draft });

  let propertiesClient = await ApiClientFactory.getPropertiesClient({
    addAccessToken: false,
  });

  try {
    const unit = await propertiesClient.get(
      Number.parseInt(unitId),
      getLocale(),
      draft
    );

    if (draft) {
      Logger.debug("Draft property received", { id: unitId, draftId: unit.id });
    } else {
      Logger.debug("Property received", unitId);
    }

    return unit;
  } catch (error) {
    if (error?.response?.status === 404 && draft) {
      return null;
    }

    throw error;
  }
};

export const getPurchaseOfferWithKey = async (
  key: string,
  purchaseOfferId: string
): Promise<DealDto> => {
  Logger.debug("Getting purchase offer", purchaseOfferId);

  const purchaseOffersClient = await ApiClientFactory.getPurchaseOffersClient();
  const purchaseOffer = await purchaseOffersClient.get(
    purchaseOfferId,
    getLocale(),
    "purchasers,selections,inclusions",
  );

  return purchaseOffer;
};

export const getPurchaseOffer = async (
  purchaseOfferId: string,
  locale: string
) => {
  let purchaseOffersClient = await ApiClientFactory.getPurchaseOffersClient();

  return await Logger.debugPerformanceAsync("getPurchaseOffer", async () => {
    return await purchaseOffersClient.get(
      purchaseOfferId || "",
      locale,
      "purchasers",
    );
  });
};

export const getProjects = async (
  key: string,
  builderId?: number,
): Promise<PaginatedResultOfProjectLookupDto> => {
    let projectsClient = await ApiClientFactory.getProjectsClient();

    const projects = await projectsClient.getProjects(
      builderId,
      "asc",
      "",
      null,
      true,
      200,
      true
    );

    return projects;
  };

export const getProject = async (
  key: string,
  projectId: string
): Promise<ProjectDto> => {
  Logger.debug(`Getting project ${projectId}`);

  let projectsClient = await ApiClientFactory.getProjectsClient();

  const projects = await projectsClient.get(
    parseInt(projectId),
    getLocale(),
    "properties,buildings,phases",
  );

  return projects;
};

export const deletePurchaseOffer = async (
  purchaseOfferId: string,
): Promise<string> => {
  Logger.debug("Deleting purchase offer", purchaseOfferId);

  let purchaseOffersClient = await ApiClientFactory.getPurchaseOffersClient();

  const result = await purchaseOffersClient.deleteDeal(purchaseOfferId);

  mutatePurchaseOffer();

  Logger.debug("Deleted purchase offer", purchaseOfferId);

  return result;
};

export const getUser = async (
  key: string,
  userId: string
 ) : Promise<UserDto> => {
  let usersClient = await ApiClientFactory.getUsersClient();
  return await usersClient.get(userId);
};

export const getUsers = async (
  key: string,
  filterByUserType: UserType,
  searchString?: string,
  page?: string,
  pageSize?: string,
) : Promise<PaginatedResultOfUserDto> => {
  let usersClient = await ApiClientFactory.getUsersClient();
  return await usersClient.getPage(
    filterByUserType,
    undefined,
    undefined,
    searchString,
    page ? Number(page) : null,
    true,
    pageSize ? Number(pageSize) : undefined,
    false,
  );
};
